"use client";

import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Button, CloseIcon, ChevronLeftIcon, ChevronRightIcon } from "./Button";
import { VideoPlayer } from "./VideoPlayer";
import { useMotionMode } from "@/context/MotionContext";

// =============================================================================
// LIGHTBOX V2
// =============================================================================
// Consolidated lightbox used by both case studies (works) and blog.
// Based on blog's implementation (portal, drag-to-pan, scroll containment),
// extended with works' features (entrance/exit animation, focus trap, YouTube,
// controls prop, wiggle at boundaries, aria-live).
//
// Item types:
//   image   — standard image with zoom/pan
//   video   — video via VideoPlayer (muted by default; set audio=true to unmute)
//   youtube — YouTube embed via <iframe>
//   prose   — scrollable text content (blog "post" type)
//
// Navigation:
//   wrapNavigation=false (default) — stops at boundaries, wiggles
//   wrapNavigation=true           — wraps around (blog behaviour)

export interface LightboxItem {
  id?: string;
  src?: string;           // image or video src
  poster?: string;        // video poster frame
  youtubeId?: string;     // YouTube video ID
  alt?: string;
  caption?: ReactNode;    // pre-rendered caption (string or JSX)
  title?: ReactNode;      // title shown above prose content
  type?: "image" | "video" | "youtube" | "prose";
  controls?: boolean;     // show native video controls (prototype walkthroughs)
  audio?: boolean;        // when true, video plays with audio (unmuted). default false.
  proseContent?: ReactNode; // scrollable body for prose posts
  meta?: ReactNode;       // optional metadata row rendered below caption (type pill, date, etc.)
}

interface LightboxV2Props {
  items: LightboxItem[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
  /** When true, navigation wraps around (last→first, first→last). Default false. */
  wrapNavigation?: boolean;
}

export function LightboxV2({
  items,
  currentIndex,
  onClose,
  onNavigate,
  wrapNavigation = false,
}: LightboxV2Props) {
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const { mode: motionMode } = useMotionMode();

  // Zoom + pan
  const [isZoomed, setIsZoomed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  // Ref (not state) so the click handler reads the latest value without a re-render race
  const didDragRef = useRef(false);

  // Wiggle at boundaries
  const [wiggleDirection, setWiggleDirection] = useState<"left" | "right" | null>(null);

  // Error
  const [hasError, setHasError] = useState(false);

  // Refs
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const proseScrollRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const previousOverflowRef = useRef<string>("");

  const current = items[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === items.length - 1;
  const showNavButtons = items.length > 1;

  // Portal mount (avoid SSR mismatch)
  useEffect(() => {
    setMounted(true);
  }, []);

  // Reduced motion preference
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Reset state on item change
  useEffect(() => {
    setHasError(false);
    setIsZoomed(false);
    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 });
    didDragRef.current = false;
  }, [currentIndex]);

  // Reset prose scroll on item change
  useEffect(() => {
    if (proseScrollRef.current) {
      proseScrollRef.current.scrollTop = 0;
    }
  }, [currentIndex]);

  // Entrance animation, body scroll lock, focus management
  useEffect(() => {
    previousActiveElementRef.current = document.activeElement as HTMLElement;
    previousOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const timer = setTimeout(() => {
      setIsVisible(true);
      closeButtonRef.current?.focus();
    }, 50);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = previousOverflowRef.current;
    };
  }, []);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      document.body.style.overflow = previousOverflowRef.current;
      previousActiveElementRef.current?.focus();
      onClose();
    }, 300);
  }, [onClose]);

  const resetZoom = useCallback(() => {
    setIsZoomed(false);
    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  const triggerWiggle = useCallback(
    (direction: "left" | "right") => {
      if (prefersReducedMotion) return;
      setWiggleDirection(direction);
      setTimeout(() => setWiggleDirection(null), 300);
    },
    [prefersReducedMotion]
  );

  const goNext = useCallback(() => {
    if (isLast) {
      if (wrapNavigation) onNavigate(0);
      else triggerWiggle("right");
      return;
    }
    onNavigate(currentIndex + 1);
  }, [currentIndex, isLast, onNavigate, wrapNavigation, triggerWiggle]);

  const goPrev = useCallback(() => {
    if (isFirst) {
      if (wrapNavigation) onNavigate(items.length - 1);
      else triggerWiggle("left");
      return;
    }
    onNavigate(currentIndex - 1);
  }, [currentIndex, isFirst, onNavigate, wrapNavigation, items.length, triggerWiggle]);

  // Keyboard navigation + focus trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "Tab") {
        // Focus trap within dialog
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleClose, goNext, goPrev]);

  // Scroll containment for prose posts.
  // Lenis + BlogOverlay both hijack window wheel events; attach a native
  // { passive: false } listener directly to the prose container and manually
  // apply the delta. stopPropagation keeps the event from bubbling up.
  // The effect is a no-op when proseScrollRef.current is null (non-prose items).
  useEffect(() => {
    const el = proseScrollRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      el.scrollTop += e.deltaY;
    };
    let touchStartY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      const deltaY = touchStartY - e.touches[0].clientY;
      touchStartY = e.touches[0].clientY;
      e.preventDefault();
      e.stopPropagation();
      el.scrollTop += deltaY;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
    };
    // mounted is included so the effect re-runs after the portal mounts; on the
    // first render the component returns null (portal gate), so proseScrollRef
    // is not yet populated. Without this, scroll never attaches for the
    // initially-opened item.
  }, [currentIndex, mounted]);

  const showModal = isVisible && !isExiting;

  // Muted: unmuted when audio=true OR when controls=true (native controls = user controls audio)
  const videoMuted = !current?.audio && !current?.controls;

  const renderMedia = () => {
    if (!current) return null;

    // Error state (image/video load failure)
    if (hasError) {
      return (
        <div
          className="flex items-center justify-center bg-[rgba(255,255,255,0.05)] border border-dashed border-[rgba(255,255,255,0.15)] rounded-xl"
          style={{ width: "min(90vw, 600px)", aspectRatio: "16/9" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col items-center gap-2 text-[rgba(255,255,255,0.3)]">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {current.type === "video" ? (
                <>
                  <rect x="2" y="2" width="20" height="20" rx="2" />
                  <path d="M10 8l6 4-6 4V8z" />
                  <line x1="4" y1="4" x2="20" y2="20" />
                </>
              ) : (
                <>
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                  <line x1="4" y1="4" x2="20" y2="20" />
                </>
              )}
            </svg>
            <span className="text-sm font-mono">Media unavailable</span>
          </div>
        </div>
      );
    }

    // Prose post: static title header + scrollable body with radial scrim between them
    if (current.type === "prose") {
      return (
        <div
          className="relative flex flex-col w-full h-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Meta — floats top-right on lg+, hidden (shown in header below) */}
          {current.meta && (
            <div className="hidden lg:flex absolute top-0 right-4 sm:right-6 z-20 items-center">
              {current.meta}
            </div>
          )}

          {/* Static header — title stays fixed as body scrolls.
              Meta shows inline on mobile, hidden on lg+ (floated top-right above). */}
          <div className="max-w-2xl mx-auto w-full flex-shrink-0 pb-3 relative z-10 px-4 sm:px-6">
            {current.title && (
              <h3 className="font-headline text-xl font-bold mb-2 leading-tight text-white">
                {current.title}
              </h3>
            )}
            {current.meta && (
              <div className="flex items-center lg:hidden">{current.meta}</div>
            )}
          </div>

          {/* Radial scrim — sits atop the scroll body, fades content scrolling under the title.
              Taller than the header so the fade zone extends into the reading area. */}
          <div
            className="absolute inset-x-0 top-0 pointer-events-none"
            style={{
              height: "220px",
              zIndex: 5,
              background:
                "radial-gradient(ellipse 120% 100% at 50% 0%, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.2) 72%, transparent 100%)",
            }}
            aria-hidden="true"
          />

          {/* Scrollable body — top mask fades content in so it dissolves rather than
              hard-cutting against the header. Bottom mask fades out before nav chrome. */}
          <div
            ref={proseScrollRef}
            className="flex-1 min-h-0 w-full overflow-y-auto overscroll-contain"
            data-lenis-prevent
            style={{
              maskImage:
                "linear-gradient(to bottom, transparent 0%, black 32px, black calc(100% - 48px), transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, transparent 0%, black 32px, black calc(100% - 48px), transparent 100%)",
            }}
          >
            <div className="max-w-2xl mx-auto pt-8 px-4 sm:px-6">
              <div className="text-[16px] leading-[1.8] text-[rgba(255,255,255,0.72)] [&_p]:mb-7 [&_p:first-child]:text-[1.125rem] [&_p:first-child]:text-[rgba(255,255,255,0.95)] [&_p:first-child]:leading-[1.7] [&_blockquote]:my-8 [&_blockquote]:text-[rgba(255,255,255,0.6)] [&_ul]:mb-7 [&_li]:mb-1.5">
                {current.proseContent}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // YouTube embed
    if (current.type === "youtube" && current.youtubeId) {
      return (
        <div
          className="relative w-[90vw] max-w-[1000px] aspect-video rounded-xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <iframe
            src={`https://www.youtube.com/embed/${current.youtubeId}?rel=0&modestbranding=1&autoplay=1&playsinline=1`}
            title={typeof current.caption === "string" ? current.caption : "YouTube video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </div>
      );
    }

    // Image or video — shared zoom/pan surface.
    // Using a wrapper means both types get identical behaviour:
    // tap to toggle 2x zoom, drag to pan while zoomed, pointer capture
    // so touch + mouse drags don't bubble to the backdrop.
    const isVideo = current.type === "video";

    return (
      <div
        className="flex items-center justify-center w-full h-full select-none touch-none"
        style={{
          overflow: isZoomed ? "visible" : "hidden",
          cursor: isZoomed ? (isDragging ? "grabbing" : "grab") : "zoom-in",
          transform: isZoomed
            ? `scale(2) translate(${dragOffset.x}px, ${dragOffset.y}px)`
            : "scale(1)",
          transformOrigin: "center center",
          transition: isDragging ? "none" : "transform 0.3s ease",
        }}
        onPointerDown={(e) => {
          // Let inner interactive children (play button) own their taps
          if ((e.target as HTMLElement).closest("button")) return;
          if (!isZoomed) return;
          e.preventDefault();
          e.stopPropagation();
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          didDragRef.current = false;
          setIsDragging(true);
          setDragStart({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
        }}
        onPointerMove={(e) => {
          if (!isDragging) return;
          e.stopPropagation();
          const newX = (e.clientX - dragStart.x) / 2; // divide by scale (2x)
          const newY = (e.clientY - dragStart.y) / 2;
          // 2px threshold: jittery tap still reads as a tap
          if (
            !didDragRef.current &&
            (Math.abs(newX - dragOffset.x) > 2 || Math.abs(newY - dragOffset.y) > 2)
          ) {
            didDragRef.current = true;
          }
          setDragOffset({ x: newX, y: newY });
        }}
        onPointerUp={(e) => {
          if (!isDragging) return;
          e.stopPropagation();
          const el = e.currentTarget as HTMLElement;
          if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
          setIsDragging(false);
        }}
        onPointerCancel={(e) => {
          const el = e.currentTarget as HTMLElement;
          if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
          setIsDragging(false);
          didDragRef.current = false;
        }}
        onClick={(e) => {
          e.stopPropagation();
          // Play button owns its own click
          if ((e.target as HTMLElement).closest("button")) return;
          // Post-drag click: swallow so pan never accidentally toggles zoom
          if (didDragRef.current) {
            didDragRef.current = false;
            return;
          }
          if (isZoomed) resetZoom();
          else setIsZoomed(true);
        }}
      >
        {isVideo ? (
          <VideoPlayer
            src={current.src!}
            poster={current.poster}
            alt={current.alt}
            caption={typeof current.caption === "string" ? current.caption : undefined}
            preload="metadata"
            controls={current.controls}
            muted={videoMuted}
            autoPlayWhenMotionOn={!current.controls}
            className="max-w-full max-h-[calc(100vh-280px)] rounded-xl object-contain pointer-events-none"
            onError={() => setHasError(true)}
          />
        ) : (
          <img
            src={current.src}
            alt={current.alt || ""}
            draggable={false}
            className="max-w-full max-h-[calc(100vh-280px)] rounded-xl object-contain pointer-events-none"
            onError={() => setHasError(true)}
          />
        )}
      </div>
    );
  };

  const content = (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={current?.alt ? `Image: ${current.alt}` : "Lightbox"}
      onClick={handleClose}
      data-lenis-prevent
      data-theme="dark"
      className={`fixed inset-0 z-[400] flex flex-col items-center justify-between ${current?.type !== "prose" ? "px-3 sm:px-6" : ""}`}
      style={{
        paddingTop: current?.type === "prose" ? "12px" : "var(--space-md)",
        paddingBottom: "var(--space-md)",
        backgroundColor: showModal ? "rgba(0,0,0,0.82)" : "rgba(0,0,0,0)",
        backdropFilter: showModal ? "blur(12px)" : "blur(0px)",
        WebkitBackdropFilter: showModal ? "blur(12px)" : "blur(0px)",
        transition: prefersReducedMotion
          ? "none"
          : "background-color var(--duration-normal) ease, backdrop-filter var(--duration-normal) ease",
      }}
    >
      {/* Screen reader announcement for item changes */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {current?.alt || "Item"} {currentIndex + 1} of {items.length}
      </div>

      {/* Media area — fixed height leaves room for metadata + buttons below.
          Prose posts carry their own meta inline, so they only need room for
          the nav row below — hence the taller reading surface. */}
      <div
        className={`relative w-full ${current?.type === "prose" ? "flex-1 min-h-0 flex flex-col" : "flex items-center justify-center"}`}
        style={{
          height: current?.type === "prose" ? undefined : "calc(100vh - 280px)",
          opacity: showModal ? 1 : 0,
          transform: prefersReducedMotion
            ? "none"
            : wiggleDirection === "left"
            ? "translateX(-8px)"
            : wiggleDirection === "right"
            ? "translateX(8px)"
            : showModal
            ? "scale(1)"
            : "scale(0.95)",
          transition: wiggleDirection
            ? "transform 0.1s ease-out"
            : prefersReducedMotion
            ? "opacity var(--duration-fast) ease"
            : "opacity var(--duration-normal) ease, transform var(--duration-normal) ease",
        }}
      >
        {renderMedia()}
      </div>

      {/* Bottom: caption + meta + nav buttons + counter */}
      <div
        className="flex flex-col items-center gap-3 px-3 sm:px-6"
        style={{
          opacity: showModal ? 1 : 0,
          transition: "opacity var(--duration-normal) ease",
        }}
      >
        {/* Caption / alt / meta — image/video only. Prose handles its own
            meta in the sticky header; no spacer needed since the media area
            is flex-1 and fills all space above the buttons. */}
        {current?.type !== "prose" && (
          <div className="flex flex-col items-center justify-start gap-1.5 min-h-[5rem]">
            {current?.alt && (
              <p
                className="text-white text-xs uppercase tracking-[0.15em] font-bold"
                aria-hidden="true"
              >
                {current.alt}
              </p>
            )}
            {current?.caption && (
              <p className="text-[rgba(255,255,255,0.7)] text-sm text-center max-w-[600px] px-4 line-clamp-3">
                {current.caption}
              </p>
            )}
            {current?.meta && (
              <div className="flex items-center justify-center">{current.meta}</div>
            )}
          </div>
        )}

        {/* Navigation: prev · close · next */}
        <div className="flex items-center gap-3">
          {showNavButtons && (
            <Button
              variant="icon-sm"
              onClick={(e) => {
                e.stopPropagation();
                goPrev();
              }}
              aria-label={isFirst && !wrapNavigation ? "At first item" : "Previous item"}
              style={{ opacity: isFirst && !wrapNavigation ? 0.3 : 1 }}
            >
              <ChevronLeftIcon />
            </Button>
          )}
          <Button
            ref={closeButtonRef}
            variant="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            aria-label="Close lightbox"
          >
            <CloseIcon />
          </Button>
          {showNavButtons && (
            <Button
              variant="icon-sm"
              onClick={(e) => {
                e.stopPropagation();
                goNext();
              }}
              aria-label={isLast && !wrapNavigation ? "At last item" : "Next item"}
              style={{ opacity: isLast && !wrapNavigation ? 0.3 : 1 }}
            >
              <ChevronRightIcon />
            </Button>
          )}
        </div>

        {/* Counter */}
        {items.length > 1 && (
          <p className="text-[rgba(255,255,255,0.4)] text-xs font-black" aria-hidden="true">
            {currentIndex + 1} / {items.length}
          </p>
        )}
      </div>
    </div>
  );

  if (!mounted) return null;
  return createPortal(content, document.body);
}
